import React from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { MessageSquare, Map, Wrench, Sparkles } from 'lucide-react'

const Process = () => {
  const { t } = useTranslation()
  const steps = [
    {
      number: "01",
      icon: <MessageSquare className="w-6 h-6" />,
      title: t('process.step1Title', "Discovery Call"),
      description: t('process.step1Desc', "We learn about your workflows, pain points, and goals. Tell us what eats your time — we tell you what can be automated."),
      duration: t('process.step1Duration', "30 minutes")
    },
    {
      number: "02",
      icon: <Map className="w-6 h-6" />,
      title: t('process.step2Title', "Solution Design"),
      description: t('process.step2Desc', "We map out your automation architecture, define integrations, and give you a clear scope with fixed pricing. No surprises."),
      duration: t('process.step2Duration', "1-2 days")
    },
    {
      number: "03",
      icon: <Wrench className="w-6 h-6" />,
      title: t('process.step3Title', "Build & Test"),
      description: t('process.step3Desc', "We build your workflow, test every edge case, and iterate until it works flawlessly. Typical delivery: 5-10 business days."),
      duration: t('process.step3Duration', "5-10 days")
    },
    {
      number: "04",
      icon: <Sparkles className="w-6 h-6" />,
      title: t('process.step4Title', "Launch & Support"),
      description: t('process.step4Desc', "We deploy, train your team, and provide ongoing support. Focus on growth — we keep the automation running."),
      duration: t('process.step4Duration', "Ongoing")
    }
  ]

  const cardVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.5,
        ease: [0.25, 0.1, 0.25, 1],
      },
    }),
  }

  return (
    <section id="process" className="py-20 sm:py-24 md:py-32 px-4 sm:px-6 bg-[#0f0f1a] relative">
      <div className="container-custom">
        <div className="text-center max-w-3xl mx-auto mb-10 sm:mb-16">
          <span className="text-purple-400 font-semibold text-sm uppercase tracking-wider">{t('process.badge', 'Our Process')}</span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mt-3 sm:mt-4 mb-4 sm:mb-6 text-white">
            {t('process.heading1', 'From Chaos to Automation in')}{' '}
            <span className="text-purple-400">{t('process.heading2', '4 Steps')}</span>
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-400">
            {t('process.description', "A proven process that delivers results fast — without the headache.")}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              custom={index}
              variants={cardVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-60px' }}
              className="relative"
            >
              <div className="h-full p-6 sm:p-8 bg-white/5 border border-white/10 rounded-2xl relative overflow-hidden group hover:border-purple-500/30 transition-all duration-300">
                {/* Number Background */}
                <div className="absolute -top-4 -left-4 text-8xl font-bold text-white/5 group-hover:text-purple-500/10 transition-colors">
                  {step.number}
                </div>
                
                <div className="relative z-10">
                  {/* Icon & Number */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-400 rounded-xl flex items-center justify-center text-white">
                      {step.icon}
                    </div>
                    <span className="text-4xl font-bold text-white/20">{step.number}</span>
                  </div>

                  <h3 className="text-xl font-semibold text-white mb-3">{step.title}</h3>
                  <p className="text-gray-400 mb-4 text-sm leading-relaxed">{step.description}</p>
                  
                  <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/5 rounded-full">
                    <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                    <span className="text-sm text-gray-300">{step.duration}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

export default Process